import React, { useMemo } from 'react'
import { styled } from 'styled-components'
import MapObject from './MapObjects/MapObject'
import {
	CLIP_PATH_URL,
	CLIP_PATH_ID,
	MAP_SIZE,
	DEFAULT_OBJECT_SIZE,
} from './helpers/constants'
import {
	getMapObjectData,
	isObjectVisible,
	forEachMapObject,
} from './helpers/functions.js'
import { Generic } from '../../../../assets/icons/map'
import type {
	Map as MapType,
	MapObject as OtherMapObjectType,
	MapIcon,
	MapInfo,
} from '@mission.io/mission-toolkit'

type Props = {
	style: {},
	missionMap: ?MapType,
	objectInfoMap: ?MapInfo,
	imageSourceResolver: string => string,
}

const OFFSET: number = 0
const CENTER = MAP_SIZE / 2
const RADIUS = (MAP_SIZE - OFFSET) / 2

const GenericIcon = () => <img src={Generic} alt="GENERIC" />

export default function Map(props: Props): React$Node {
	const map = props.missionMap
	const viewBox = '0 0 ' + MAP_SIZE + ' ' + MAP_SIZE
	const mapObjects = useMemo(
		() => (map ? getObjectsToRender(map, props.objectInfoMap) : []),
		[map, props.objectInfoMap]
	)
	const backgroundImage = map ? map.backgroundImage : null
	return (
		<MapContainer style={props.style}>
			<svg
				viewBox={viewBox}
				preserveAspectRatio="xMidYMid meet"
				width="100%"
				height="100%"
				overflow="visible">
				<defs>
					<circle id="circle" cx={CENTER} cy={CENTER} r={RADIUS} />
				</defs>
				<clipPath id={CLIP_PATH_ID}>
					<use xlinkHref="#circle" />
				</clipPath>
				<circle cx={CENTER} cy={CENTER} r={RADIUS} fill="#14163d" />
				{backgroundImage && (
					<image
						clipPath={CLIP_PATH_URL}
						href={props.imageSourceResolver(backgroundImage)}
						height="100%"
					/>
				)}
				{mapObjects}
			</svg>
		</MapContainer>
	)
}

function getObjectsToRender(
	map: MapType,
	objectInfoMap: ?MapInfo
): React$Node[] {
	const mapObjects: React$Node[] = []
	forEachMapObject(map, (key, mapObject) => {
		const object = objectInfoMap?.[map._id]?.objectInfo[mapObject._id]
		if (!object) {
			return
		}
		let component = GenericIcon
		let sizeKey: ?{ width: number, height: number }
		let shouldCenterCoordinates: boolean = true
		let mapObjectType = 'OTHER'
		// $FlowFixMe flow doesn't recognize the type cast
		const other: OtherMapObjectType = mapObject
		if (other.icon) {
			const icon = other.icon
			component = getSVGIcon(icon)
			sizeKey = { width: icon.width, height: icon.height }
		}
		const { sizes, centeredX, centeredY, viewX, viewY } = getMapObjectData(
			mapObjectType,
			mapObject,
			sizeKey,
			shouldCenterCoordinates
		)
		mapObjects.push(
			<MapObject
				visibleToStudents={isObjectVisible(object)}
				key={mapObject._id}
				component={component}
				mapObjectId={mapObject._id}
				sizes={sizes}
				centeredX={centeredX}
				centeredY={centeredY}
				viewX={viewX}
				viewY={viewY}
			/>
		)
	})
	return mapObjects
}

const getSVGIcon = (icon: MapIcon) => {
	const svgIcon = (props: {}) => (
		<svg {...props}>
			<image
				xlinkHref={icon.url}
				height={icon.height || DEFAULT_OBJECT_SIZE}
				width={icon.width || DEFAULT_OBJECT_SIZE}
			/>
		</svg>
	)
	return svgIcon
}

const MapContainer = styled.div`
	height: 95%;
	width: 100%;
	z-index: ${({ theme }) => theme.zVisible};
`
